import { type JSX, type ReactNode, forwardRef } from 'react';

import { Box, Button, CircularProgress, type ButtonProps } from '@mui/material';

const SPINNER_SIZE = 16;

export interface SubmitButtonProps extends Omit<ButtonProps, 'children'> {
  loading?: boolean;
  children: ReactNode;
}

/**
 * Button wrapper that swaps its label for a centered spinner during
 * submit while preserving label-driven width (ghost label keeps the
 * box dimensions stable). Disabled while loading so the user cannot
 * double-submit. Pairs with `<DialogSubmitOverlay>` on dialogs.
 */
export const SubmitButton = forwardRef<HTMLButtonElement, SubmitButtonProps>(
  (
    { loading = false, disabled, children, ...rest }: SubmitButtonProps,
    ref,
  ): JSX.Element => (
    <Button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading}
      data-loading={loading ? 'true' : 'false'}
      {...rest}
    >
      <Box
        component="span"
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '1ch',
        }}
      >
        <Box
          component="span"
          sx={{
            visibility: loading ? 'hidden' : 'visible',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {children}
        </Box>
        {loading ? (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={SPINNER_SIZE} color="inherit" />
          </Box>
        ) : null}
      </Box>
    </Button>
  ),
);

SubmitButton.displayName = 'SubmitButton';
